import React, { useState, useEffect, useMemo } from "react";
import { ConfigProvider } from "antd";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchProfitMargins, fetchStores } from "./store/profitMarginsSlice";
import { useTheme } from "../../../context/ThemeContext";
import { getAntdThemeConfig } from "../../../utils/antdTheme";
import moment from "moment";
import { MainContainer } from "./styles";
import { DateRange, MetricOption, Merchant, User } from "./types";
import ProfitHeader from "./ProfitHeader";
import ProfitSummary from "./ProfitSummary";
import ProfitChart from "./ProfitChart";

const ProfitMargins: React.FC = () => {
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { profitData, loading, stores } = useSelector((state: RootState) => state.profitMargins);
  
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [storeId, setStoreId] = useState<string>("");
  const [metricOption, setMetricOption] = useState<MetricOption>("grossProfit");
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: moment("2023-01-01"),
    endDate: moment(),
  });
  const [chartType, setChartType] = useState<string>("line");

  const user: User = JSON.parse(localStorage.getItem("user") || "{}");
  const themeMode: "light" | "dark" = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  const startDateStr = useMemo(() => {
    return selectedDateRange.startDate 
      ? moment(selectedDateRange.startDate).format("YYYY-MM-DD")
      : null;
  }, [selectedDateRange.startDate]);

  const endDateStr = useMemo(() => {
    return selectedDateRange.endDate
      ? moment(selectedDateRange.endDate).format("YYYY-MM-DD")
      : null;
  }, [selectedDateRange.endDate]);

  const dateRangeParams = useMemo(() => {
    if (!startDateStr || !endDateStr) return null;
    return {
      startDate: startDateStr,
      endDate: endDateStr,
    };
  }, [startDateStr, endDateStr]);


  useEffect(() => {
    if (user?.role === "merchant" && !merchant) {
      setMerchant(user as Merchant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    if (user?.role !== "admin") {
      dispatch(fetchStores());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, dispatch]);

  useEffect(() => {
    if (stores.length > 0 && !storeId) {
      setStoreId(stores[0].id);
    }
  }, [stores, storeId]);

  useEffect(() => {
    if (!dateRangeParams) return;

    const params = {
      ...dateRangeParams,
      store: storeId,
      merchantId: merchant?.id,
    };

    dispatch(fetchProfitMargins(params));
  }, [dispatch, dateRangeParams, merchant?.id, storeId]);

  return (
    <StyledThemeProvider theme={styledTheme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <MainContainer>
          <ProfitHeader
            merchant={merchant}
            storeId={storeId}
            user={user}
            onMerchantChange={setMerchant}
            onStoreChange={(store) => setStoreId(store.id)}
          />

          <ProfitSummary 
            selectedDateRange={selectedDateRange}
            onDateRangeChange={setSelectedDateRange}
          />

          <ProfitChart
            profitData={profitData}
            selectedDateRange={selectedDateRange}
            chartType={chartType}
            metricOption={metricOption}
            isLoading={loading}
            onDateRangeChange={setSelectedDateRange}
            onDateSelect={setSelectedDateRange}
            onMetricChange={(option) => setMetricOption(option as MetricOption)}
            onChartTypeChange={setChartType}
          />
        </MainContainer>
      </ConfigProvider>
    </StyledThemeProvider>
  );
};

export default ProfitMargins;
